import { cloneDeep, debounce, isEmpty } from "lodash";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { ParentScreenCtx } from "../../pages/workbench_build/WorkbenchBuild";
import { Card, Checkbox, FormControl, FormControlLabel, InputLabel, OutlinedInput, Stack, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { eachDeep } from "deepdash-es/standalone";
import { treeActions } from "../../redux/slices/treeSlice";
import { validateWorkflowItemName } from "../../utils/utilFunctions";
import ErrorModal from "./Forms/FieldDataValidation/ErrorModal";

function WorkflowCommentProperties() {
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const selectedNode = rightTree.tree[rightTree.currentTab].selectedNode;
  const translatedData = rightTree.tree[rightTree.currentTab].translatedData;
  const currentTab = rightTree.currentTab;
  const wfItemId = selectedNode?.ID;
  const dispatch = useDispatch();
  const [nameInput, setNameInput] = useState(selectedNode?.NAME || '');
  const [comment, setComment] = useState(selectedNode?.COMMENT || '');
  
  // Name validation states
  const [nameErrorModal, setNameErrorModal] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState('');
  
  const updateData = (key, value) => {
   if(!key) return;
   
   const updatedData = eachDeep(cloneDeep(translatedData), (obj) => {
    if(obj?.key !== selectedNode?.key) return;
    if(key === "NAME") {
      obj.title = value;
    } else if(key === "LOG_WFITEM_DETAILS") {
      obj.ENABLED = value;
    }
    obj[key] = value;
    
    dispatch(treeActions.setRightTreeSelectedNode({
      screen: parentScreen,
      type: rightTree.currentTab,
      data: obj
    }))
   })
   
   dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: rightTree.currentTab,
        data: updatedData
      })
    )
  }
  const debounceChange = useCallback(debounce(updateData,220), [rightTree.tree]);

  const handleNameChange = (e) => {
    const { value} = e.target;
    setNameInput(value);
  };
  
  const handleNameBlur = (value, e) => {
    const oldName = selectedNode?.NAME || '';
    
    const validationResult = validateWorkflowItemName(
      value, 
      oldName, 
      translatedData, 
      selectedNode?.key, 
      'COMMENT'
    );
    
    if (validationResult.isValid) {
      const newName = value?.trim() || '';
      setNameInput(newName);
      if (newName !== oldName) {
        debounceChange("NAME", newName);
        // Wait for debounce delay + buffer before dispatching
        setTimeout(() => {
          dispatch(treeActions.setIsInputFocusedFalse());
        }, 300);
      } else {
        // If no change, dispatch immediately
        setTimeout(() => {
          dispatch(treeActions.setIsInputFocusedFalse());
        }, 100);
      }
    } else {
      if (validationResult.errorMessage) {
        setNameErrorMessage(t(validationResult.errorMessage));
        setNameErrorModal(true);
      } else if (validationResult.shouldRevert) {
        setNameInput(oldName);
      }
      // For error cases, dispatch immediately
      setTimeout(() => {
        dispatch(treeActions.setIsInputFocusedFalse());
      }, 100);
    }
  };

  const handleNameErrorModal = (isOpen) => {
    setNameErrorModal(isOpen);
    if (!isOpen) {
      const oldName = selectedNode?.NAME || '';
      setNameInput(oldName);
    }
  };
  const handleDonotLogWfItemDetailsChange = (e, checked) => {
  
    const isInvertedKey = e.target.name === "LOG_WFITEM_DETAILS" ;
    const booleanToString = isInvertedKey
      ? checked
        ? "FALSE"
        : "TRUE"
      : checked
      ? "TRUE"
      : "FALSE";

    updateData('LOG_WFITEM_DETAILS', booleanToString);
  }
  
  const handleCommentChange = (e) => {
    const value = e.target.value;
    setComment(value);
    debounceChange("COMMENT", value);
  };

  useEffect(() => {
    setNameInput(selectedNode?.NAME || '');
    setComment(selectedNode?.COMMENT || '');
  }, [selectedNode?.key]);

  return (
    <>
      {!isEmpty(rightTree.tree[currentTab].selectedNode) ? (
        <Stack spacing="15px" className="tp-segment">
          <FormControl variant="outlined" className="width-100" size="small">
            <InputLabel
              htmlFor="name-input"
              sx={{ padding: "0 6px !important", left: "-5px" }}>
              {t("NAME")}
            </InputLabel>
            <OutlinedInput
              id="name-input"
              size="small"
              variant="outlined"
              fullWidth
              autoComplete="off"
              value={nameInput}
              endAdornment={
                <div style={styles.nameAdornment}>{`ID: ${wfItemId}`}</div>
              }
              sx={{ justifyContent: "flex-end" }}
              onChange={(e) => handleNameChange(e)}
               onBlur={(e) => handleNameBlur(e.target.value, e)}
               onFocus={() => dispatch(treeActions.setIsInputFocusedTrue())}
            />
          </FormControl>
          <FormControlLabel
            label={t("LBL_LOG_NO_WFITEM_DETAILS")}
            sx={{ height: "30px" }}
            className={"width-fit-content"}
            control={
              <Checkbox
                checked={selectedNode?.LOG_WFITEM_DETAILS === 'FALSE' || false}
                size="small"
                name="LOG_WFITEM_DETAILS"
                onChange={(e, checked) =>
                  handleDonotLogWfItemDetailsChange(e,checked)
                }
              />
            }
          />
          <InputLabel
            htmlFor="comment">{t('LBL_COMMENT')}</InputLabel>
            <TextField
              id="comment"
              multiline
              rows={7}
              fullWidth
              variant="outlined"
              size="small"
              autoComplete="off"
              value={comment}
              onChange={(e) => handleCommentChange(e)}
            />
        </Stack>
      ) : (
        <div className="height-100">
          <div className="panel-no-data">{t("NO_FLD_SELECTED")}</div>
        </div>
      )}

      {/* Error Modal for name validation */}
      <ErrorModal
        errorModal={nameErrorModal}
        setErrorModal={setNameErrorModal}
        errorMessage={nameErrorMessage}
        errorType={t('ERROR')}
        handleErrorModal={handleNameErrorModal}
      />
    </>
  );
}

export default WorkflowCommentProperties;
const styles = {
  nameAdornment: {
    color: "#666666",
    fontSize: "9px",
    position: "absolute",
    right: "13px",
    top: "-6px",
    backgroundColor: "#FFF",
    zIndex: "2",
    padding: "0 5px",
    height: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};
